import { BusinessPartnerClassification, CustomerSupplierCategory, ELinkCement } from './enums';

import type { ContactRoleType } from '@/features/companies/types/company.types';

import {
  EPlanFormulationProductTypeLabel,
  EPlanMaterialTypeLabel,
  EPlanOrderStatusLabel,
  EPlanQuantitySuffixLabel,
  EPlanVehicleSizeLabel,
} from '@/features/plans/types/plan.enum';
import {
  PropertyCheckMethod,
  PropertyStructuralFormat,
  PropertyVehicleSize,
} from '@/features/properties/types/property.types';

const NULL_DATA = 'null';

const BUSINESS_PARTNER_LABELS = {
  [BusinessPartnerClassification.CUSTOMER]: '得意先',
  [BusinessPartnerClassification.SUPPLIER]: '仕入先',
  [BusinessPartnerClassification.CONSTRUCTION_COMPANY]: '建設会社',
  [BusinessPartnerClassification.WITNESS_COMPANY]: '立会会社',
};

const CUSTOMER_SUPPLIER_LABELS = {
  [CustomerSupplierCategory.GENERAL_CONTRACTORS]: 'ゼネコン',
  [CustomerSupplierCategory.HOUSE_MANUFACTURERS]: 'ハウスメーカー',
  [CustomerSupplierCategory.TRADING_COMPANIES]: '商社販売店',
  [CustomerSupplierCategory.READY_MIX_CONCRETE_PLANTS]: '生コンプラント',
  [CustomerSupplierCategory.OTHERS]: 'その他',
};

const CONTACT_ROLE_TYPES: readonly ContactRoleType[] = ['営業', '試験', '出荷', '予定', '実績'];

export { NULL_DATA, BUSINESS_PARTNER_LABELS, CUSTOMER_SUPPLIER_LABELS, CONTACT_ROLE_TYPES };

export const PARTNER_CLASSIFICATIONS = {
  CUSTOMER: 1,
  SUPPLIER: 2,
  CONSTRUCTION_COMPANY: 3,
  WITNESS_COMPANY: 4,
};

export const MESSAGES = {
  SUCCESS_LOGOUT: 'ログアウトしました。',
  REGISTER_SUCCESS: '予定を登録しました。',
  EDIT_SUCCESS: '予定情報を更新しました。',
  EMAIL_REQUIRED: '必須入力です。メールアドレスを入力ください。',
  EMAIL_INVALID: ' 正しい形式でメールアドレスを入力してください。',
  UNAUTHORIZED: 'ログインできません。<br/>有効なメールアドレスとパスワードを入力ください。',
  TIMEOUT_AUTH: 'タイムアウトしました。<br/>再度ログインください。',
  TIMEOUT: 'タイムアウトしました。<p style="margin-top: 7px">再度ログインください。</p>',
  ENTER_SUGGEST: '現場略称の入力はサジェストから選択ください。',
  ACCESS_DENY: '権限がありません',
  NOT_FOUND: 'データが存在しません。',
  REGISTER_PROPERTY_SUCCESS: '現場を登録しました。',
  CONFIRM_LEAVE_PAGE: '入力をキャンセルします。よろしいですか？',
  EDIT_PROPERTY_SUCCESS: '現場情報を変更しました。',
  DELETE_RECEIPT_SUCCESS: '受領資料を削除しました。',
  ARCHIVED: '現場をアーカイブしました。',
  UN_ARCHIVED: '現場をアクティブ化しました。',
  DELETE_PROPERTY: '現場を削除しました。',
  BTN_ARCHIVE: 'アーカイブ',
  BTN_UN_ARCHIVE: 'アクティブに戻す',
  REGISTER_RECEIPT_SUCCESS: '受領資料を登録しました。',
  CONFIRM_LEAVE_PROPERTY_PAGE: '入力内容を破棄して一覧画面へ戻ります。',
  REGISTER_ERROR: '入力エラーがあります。エラーメッセージをご確認のうえ修正ください',
  BE_REGISTER_ERROR: '何らかの理由で登録ができません。再度操作してください。',
  CONSTRUCTION_COMPANY_REGISTER_SUCCESS: '建設会社を登録しました。',
  CONSTRUCTION_COMPANY_UPDATE_SUCCESS: '建設会社を変更しました。',
  CUSTOMER_HAS_BEEN_REGISTERED: '得意先を登録しました。',
  CUSTOMER_HAS_BEEN_CHANGED: '得意先を変更しました。',
  POSTAL_CODE_INVALID: '郵便番号（前半）と郵便番号（後半）の一方のみの登録はできません。',
  SUPPLIER_HAS_BEEN_CHANGED: '仕入先を変更しました。',
  SUPPLIER_HAS_BEEN_REGISTERED: '仕入先を登録しました。',
  WITNESS_COMPANY_HAS_BEEN_CHANGED: '立会会社を変更しました。',
  WITNESS_COMPANY_HAS_BEEN_REGISTERED: '立会会社を登録しました。',
  MEMBER_HAS_BEEN_REGISTERED: '担当者を登録しました。',
  MEMBER_HAS_BEEN_CHANGED: '担当者情報を変更しました。',
  DUPLICATE_ID: '登録済みの担当者IDです。',
  DUPLICATE_EMAIL: '登録済みのメールアドレスです。',
  DELETE_STAFF_SUCCESS: '担当者を削除しました。',
  RESTORE_STAFF_SUCCESS: '担当者を復帰しました。',
  REINVITE_STAFF_SUCCESS: '再招待メールを送信しました。',
  DELETE_WITNESS_COMPANY: '立会会社を削除しました。',
  DELETE_CUSTOMER: '得意先を削除しました。',
  DELETE_CONSTRUCTION_COMPANY: '建設会社を削除しました。',
  DELETE_SUPPLIER: '仕入先を削除しました。',
  CUSTOMER_SUPPLIER_NOT_EXIST: '得意先仕入先は存在しません。',
  WITNESS_COMPANY_NOT_EXIST: '立会会社は存在しません。',
  CONFIRM_LEAVE_PAGE_UNSAVED_CHANGES: '変更内容が保存されていません。このページを離れますか？',
  CONFIRM_CHANGES:
    '入力した情報を再度ご確認の上、「更新」ボタンをクリックしてください。\n変更が必要な場合は、直接編集することができます。',
};

export const EXCLUSION_DEPARTMENTS = ['企画開発室'];
export const OTHER_DEPARTMENT_CODE = 99;
export const DISABLED_DEPARTMENT_CODE = 282300;

// Time format regex: HH:MM (00:00 to 23:59)
const timeFormatRegex = /^[0-2][0-9]:[0-5][0-9]$/;

// Half-width numeric: up to 7 integer digits and up to 2 decimal digits
const quantityFormatRegex = /^\d{1,7}(\.\d{1,2})?$/;

// Helper to validate quantity format (max 7 digits integer part, max 2 digits decimal part)
const validateQuantity = (
  value: number | string | null | undefined,
  required: boolean = true
): boolean => {
  if (value === null || value === undefined || value === '') {
    return !required;
  }

  const strValue = typeof value === 'number' ? String(value) : String(value).trim();
  return quantityFormatRegex.test(strValue);
};

export const PLAN_FIELDS = {
  ORDER_STATUS: {
    id: 1,
    maxLength: 10,
    validate: (value: any) => {
      return value !== null && value !== undefined && value !== '';
    },
    message: 'ステータスは必須入力項目です。',
    values: Object.values(EPlanOrderStatusLabel),
  },
  PLANT_ID: {
    id: 2,
    validate: (value: any) => {
      return value !== null && value !== undefined && value !== '';
    },
    message: 'プラントは必須入力項目です。\nプラント名の入力はサジェストから選択ください。',
  },
  PLACEMENT_DATE: {
    id: 3,
    validate: (value: any) => {
      return value !== null && value !== undefined && value !== '';
    },
    message: '打設日は必須入力項目です。',
  },
  TIME_HOPE: {
    id: 4,
    maxLength: 65000,
    validate: (value: any) => {
      if (!value) return true;
      return timeFormatRegex.test(value);
    },
    message: '可能時間はHH:MM（半角）の形式で入力くだ',
  },
  TIME_POSSIBLE: {
    id: 5,
    maxLength: 65000,
    validate: (value: any) => {
      if (!value) return false;
      return timeFormatRegex.test(value);
    },
    message: '可能時間は必須入力項目です。\n可能時間はHH:MM（半角）の形式で入力ください。',
  },
  TIME_POSSIBLE_REMARKS: {
    id: 6,
    maxLength: 256,
    validate: () => true,
  },
  PLACE_TO_PLACE: {
    id: 7,
    maxLength: 100,
    validate: () => true,
  },
  QUANTITY: {
    id: 8,
    validate: (value: any) => {
      return validateQuantity(value);
    },
    message:
      '数量は必須入力項目です。\n数量の値は半角数値で入力ください。\n数量には整数7桁まで、小数点以下2桁までで入力してください。',
  },
  QUANTITY_SUFFIX: {
    id: 9,
    validate: () => true,
    defaultValue: EPlanQuantitySuffixLabel.NONE,
    values: Object.values(EPlanQuantitySuffixLabel),
  },
  QUANTITY_UNIT: {
    id: 10,
    maxLength: 2,
  },

  FORMULATION_PRODUCT_TYPE: {
    id: 11,
    values: Object.values(EPlanFormulationProductTypeLabel),
  },
  FORMULATION_PRODUCT: {
    id: 12,
    maxLength: 256,
    validate: () => true,
  },
  VEHICLE_SIZE: {
    id: 13,
    validate: (value: any) => {
      return value !== null && value !== undefined && value !== '';
    },
    message: '車両は必須入力項目です。',
    values: Object.values(EPlanVehicleSizeLabel),
  },
  PRECEDING_MATERIAL_TYPE: {
    id: 14,
    validate: () => true,
    defaultValue: EPlanMaterialTypeLabel.NONE,
    values: Object.values(EPlanMaterialTypeLabel),
  },
  PRECEDING_MATERIAL_QUANTITY: {
    id: 15,
    validate: (value: any) => {
      return validateQuantity(value, false);
    },
    message:
      '数量の値は半角数値で入力ください。\n数量には整数7桁まで、小数点以下2桁までで入力してください。',
  },
  PITCH_HOPE: {
    id: 16,
    maxLength: 100,
    validate: () => true,
  },
  PITCH_POSSIBLE: {
    id: 17,
    maxLength: 100,
    validate: () => true,
  },
  PLACING_METHOD: {
    id: 18,
    maxLength: 20,
    validate: () => true,
  },
  WITNESS_COMPANY_ID: {
    id: 19,
    validate: () => true,
    message: '提案の中から証人会社を選択してください。',
  },
  WITNESS_NAME: {
    id: 20,
    maxLength: 50,
    validate: () => true,
  },
  WITNESS_TEL: {
    id: 21,
    maxLength: 20,
    validate: () => true,
  },
  REMARKS_FOR_CLIENT: {
    id: 22,
    maxLength: 256,
    validate: () => true,
  },
  REMARKS: {
    id: 23,
    maxLength: 65000,
    validate: () => true,
  },
  PLAN_ID: {
    id: 24,
    validate: () => true,
  },
  IS_DELETED: {
    id: 25,
    validate: () => true,
  },
  IS_ARCHIVE: {
    id: 26,
    validate: () => true,
  },
};

export const FORMAT_FULL_YEAR = 'YYYY/MM/DD';

export const SELECTED_PLAN_ID_PARAM = 'selectedPlanId';

export const TYPE_BOOLEAN = {
  TRUE: '1',
  FALSE: '0',
};

export const PUBLIC_PRIVATE_CLASSIFICATION_OPTIONS = [
  { value: 1, label: '民間' },
  { value: 2, label: '官庁' },
];
// Vehicle size options
export const VEHICLE_SIZE_OPTIONS: { value: PropertyVehicleSize; label: string }[] = [
  { value: 1, label: '大型' },
  { value: 2, label: '中型' },
  { value: 3, label: '小型' },
  { value: 4, label: 'm³車' },
  { value: 5, label: '引取' },
];

// Check method options
export const CHECK_METHOD_OPTIONS: { value: PropertyCheckMethod; label: string }[] = [
  { value: 1, label: 'T' },
  { value: 2, label: 'F' },
  { value: 3, label: 'TF' },
  { value: 4, label: 'HP' },
];

export const LINK_CEMENT_TEXT = {
  [ELinkCement.TARGET]: '対象',
  [ELinkCement.NOT_TARGET]: '対象外',
};

// Structural format options
export const STRUCTURAL_FORMAT_OPTIONS: { value: PropertyStructuralFormat; label: string }[] = [
  { value: 1, label: 'RC' },
  { value: 2, label: 'SRC' },
  { value: 3, label: 'S' },
  { value: 4, label: 'WRC' },
  { value: 5, label: 'その他' },
];

export const STRUCTURAL_FORMAT_OTHER_VALUE = 5;

export const PURCHASE_ORDER_OPTIONS: { value: string; label: string }[] = [
  { value: 'true', label: '有' },
  { value: 'false', label: '無' },
];
